import { useCallback, useEffect, useMemo, useState } from "react";

import { TInputTag } from "components/InputMultipleTag";

import { TGetBUUnassignedMemberFilter } from "../../Model/types";
import { MEMBER_TYPE_TAG_OPTIONS } from "../../Model/constants";

interface UseEmployeeFilterProps {
  filter?: Partial<TGetBUUnassignedMemberFilter>;
  onFilter?: (filter: Partial<TGetBUUnassignedMemberFilter>) => void;
}

const useEmployeeFilter = ({ filter, onFilter }: UseEmployeeFilterProps) => {
  const [openFilter, setOpenFilter] = useState(false);
  const [memberTypeTags, setMemberTypeTags] = useState<TInputTag[]>([]);

  const isEmptyFilter = useMemo(() => {
    const existingFilter: Partial<TGetBUUnassignedMemberFilter> = {
      memberType: filter?.memberType,
    };

    for (const key in existingFilter) {
      if (existingFilter?.[key as keyof TGetBUUnassignedMemberFilter])
        return false;
    }

    return true;
  }, [filter]);

  const handleSubmitFilter = () => {
    onFilter?.({
      memberType: memberTypeTags?.map((m) => m.id)?.join(","),
    });
    setOpenFilter(false);
  };

  const handleResetFilter = useCallback(() => {
    setMemberTypeTags([]);

    onFilter?.({
      memberType: undefined,
    });
  }, [onFilter]);

  const handleOpenFilter = () => {
    setOpenFilter((prev) => !prev);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleCloseAndResetFilter = useCallback(() => {
    handleCloseFilter();
    handleResetFilter();
  }, [handleResetFilter]);

  useEffect(() => {
    if (filter?.memberType) {
      const statusParams = filter.memberType.split(",");
      const filteredStatus = MEMBER_TYPE_TAG_OPTIONS.filter((t) =>
        statusParams.includes(t.id)
      );
      setMemberTypeTags(filteredStatus);
    }
  }, [filter]);

  return {
    openFilter,
    isEmptyFilter,
    memberTypeTags,
    hasFilterValue: !!memberTypeTags.length,
    setMemberTypeTags,
    onCloseFilter: handleCloseFilter,
    onOpenFilter: handleOpenFilter,
    onSubmitFilter: handleSubmitFilter,
    onResetFilter: handleResetFilter,
    onCloseAndResetFilter: handleCloseAndResetFilter,
  };
};

export default useEmployeeFilter;
